import { useEffect, useState, type ReactNode } from 'react';
import { FeatureGate } from '../../components/FeatureGate';
import { useFeature } from '../../lib/features';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ExternalLink, Info, MessageCircle, Share2, SquarePen } from 'lucide-react';
import { useChannelFeed, useStore } from '../../store/StoreContext';
import { useCueDiagnostics } from '../../lib/cue/useCueDiagnostics';
import { useActivityBlink } from '../../lib/cue/useActivityBlink';
import { useWideLayout } from '../../layouts/AppLayout';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { IconButton } from '../../components/ui/IconButton';
import { AvatarView } from '../../components/ui/AvatarView';
import { ChannelDataMenu } from '../../components/channels/ChannelDataMenu';
import { SyncButton } from '../../components/ui/SyncButton';
import { EmptyState } from '../../components/ui/EmptyState';
import { CHANNEL_VIEW_TYPES, label as capitalize, type ChannelViewType } from '../../types';
import { MessagesScreen } from './MessagesScreen';
import { ChartScreen } from './ChartScreen';
import { ReportsScreen } from './ReportsScreen';

// Below the `xl` breakpoint there isn't room for three real columns (a
// message compose bar, a chart, and report cards all need genuine width to
// stay usable - see paneClass below), so this stays a single-pane-at-a-
// time tab switcher there, exactly as before. At `xl` and up (1280px -
// comfortably fits three ~300px+ columns once useWideLayout below frees up
// the sidebar-adjusted content width), all three panes render side by side
// instead and the tab switcher itself hides, since there's nothing left to
// switch between - unless threeColumnLayout (Settings > Appearance,
// persisted to localStorage) is off, in which case every `xl:` class below
// is simply omitted so it stays the single-pane tab view regardless of how
// wide the screen actually is. Tailwind's `xl:` prefix is hardcoded
// directly into every className below rather than built from a shared
// variable - its JIT compiler only picks up classes it can see as literal
// strings in source, so the on/off split has to happen by choosing between
// two literal strings, not by interpolating the prefix itself.
export function ChatScreen() {
  const { channelId } = useParams();
  const [searchParams] = useSearchParams();
  // Set by a tapped push notification's deep link (?signal=<id>) - the
  // Messages pane scrolls to it if it's in the loaded history (best-effort).
  const focusSignalId = searchParams.get('signal') ?? undefined;
  const navigate = useNavigate();
  const publicPagesOn = useFeature('channels.public_pages');
  const { channel, ensureChannelLoaded, updateChannelViewType, loadMessages, subscribeToChannel, markChannelViewed, threeColumnLayout, isChannelOwner, manualRefresh } = useStore();
  const c = channel(channelId ?? '');
  const feedLength = useChannelFeed(channelId).messages.length;
  // Which pane a non-owner is currently LOOKING at, entirely local and
  // never persisted - see the SegmentedControl's onChange below for why
  // this exists at all: switching tabs used to always go through
  // updateChannelViewType -> patchChannel, which writes the channel's own
  // stored viewType (an owner-only field, channel/update.wsl 403s anyone
  // else). patchChannel also only ever mutates the `channels` array, so
  // for a channel this account hasn't followed/joined (lives only in
  // viewedChannels - see ensureChannelLoaded's comment) it was a silent
  // no-op: no error, no local change either, so a non-follower's Messages/
  // Charts/Reports tab clicks visibly did nothing at all. Reset to null
  // (fall back to the channel's own configured pane) on every channel
  // change so switching channels doesn't carry over a stale override.
  const [localViewType, setLocalViewType] = useState<ChannelViewType | null>(null);
  useEffect(() => {
    setLocalViewType(null);
  }, [channelId]);

  // A public channel opened straight from its URL (or the Details screen's
  // "Open chat") isn't in `channels` - pull it into viewedChannels so this
  // screen can render it read-only. Same fallback ChannelDetailsScreen
  // uses; loadMessages/loadMoreMessages now merge into viewedChannels too.
  useEffect(() => {
    ensureChannelLoaded(channelId ?? '');
  }, [channelId, ensureChannelLoaded]);
  // Opts out of the app's default centered max-w-3xl column - three real
  // columns need the full content width the sidebar leaves, not a third of
  // a 768px one. Only while threeColumnLayout is actually on, though: with
  // it off this screen never lays out side by side at any width (see the
  // xl: gating below), so it has no reason to break out of the normal
  // centered column either - doing so unconditionally was making a
  // single-pane chat suddenly snap to full width for no visible reason.
  useWideLayout(threeColumnLayout);

  // Pulls message history from the backend on open (so a message sent from
  // another client while this screen was closed shows up too - see
  // StoreContext.tsx's loadMessages comment), then joins this channel's Cue
  // room for the rest of the visit so a message sent while it's open shows
  // up live instead of waiting for the next visit. A report is just a
  // message tagged type: "report" (see ReportsScreen.tsx), so this single
  // load/subscribe pair covers both tabs.
  useEffect(() => {
    if (!channelId) return;
    // In manual-refresh mode (Settings > Connection) skip the history
    // pull - the cached messages render and the Sync button below fetches
    // on demand - but still join the live Cue room (sending/receiving is
    // never suppressed) and mark the channel read.
    if (manualRefresh) markChannelViewed(channelId);
    else loadMessages(channelId).then(() => markChannelViewed(channelId));
    return subscribeToChannel(channelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  // A live message that lands while this channel is open is read the moment
  // it appears - re-mark so its badge doesn't briefly reappear after the
  // relay's refreshUnread().
  useEffect(() => {
    if (channelId && c) markChannelViewed(channelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedLength]);

  if (!c) {
    return <EmptyState icon={MessageCircle} title="No channel" subtitle="Select a channel first." />;
  }

  // 'posts' is hidden from the tab set (Posts feature paused for now, see
  // types.ts) but may still be stored on older data - fall back to Reports,
  // the new default, rather than showing a blank pane.
  const configuredViewType = c.viewType === 'posts' ? 'reports' : c.viewType;
  // A non-owner's own local pane choice wins over the channel's configured
  // default once they've picked one - see localViewType's own comment.
  const viewType = localViewType ?? configuredViewType;
  const owner = isChannelOwner(c);
  const changeView = (v: ChannelViewType) => {
    if (owner) updateChannelViewType(v, c.id);
    else setLocalViewType(v);
  };

  return (
    // Desktop: fill from just below the content wrapper's top padding
    // (1.5rem) down to the top of the site-wide fixed footer (~2.5rem) -
    // and -mb-6 eats the wrapper's bottom padding so the compose bar sits
    // flush against the footer, no gap. Mobile is unchanged (no footer;
    // 8rem = header + bottom tab bar).
    <div className="flex flex-col gap-4 h-[calc(100vh-5rem)] md:h-[calc(100vh-4rem-var(--ask-dock-h,0px))] md:-mb-6">
      <div className="flex items-center gap-2">
        {/* Real browser-history back, matching ScreenHeader's own default
            on every other channel sub-screen (details/edit/share) -
            not a hardcoded '/channels'. A channel can be opened from
            either the Channels list or Showcase, and a hardcoded target
            always sent you to Channels even when you'd come from
            Showcase, which both landed you on the wrong screen and threw
            away Showcase's scroll position/results (it unmounts on
            navigate-away - see ShowcaseScreen.tsx's own cache comment). */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-black/5 dark:hover:bg-white/10 -ml-2 shrink-0"
          aria-label="Back"
        >
          <ChevronLeft size={22} />
        </button>
        <AvatarView symbol={c.avatarSymbol} colorHex={c.accentHex} imageKey={c.avatarImageKey} size={28} />
        <h1 className="text-xl font-bold tracking-tight m-0 flex-1 truncate">{c.title}</h1>
        {/* One sync button covers all three tabs below (Messages/Charts/
            Reports) rather than duplicating it in each - all three read off
            c.messages (loadMessages), since a report is just a message
            tagged type: "report". */}
        {manualRefresh && <SyncButton onSync={() => loadMessages(c.id)} ariaLabel="Sync signals" />}
        {/* Sharing a channel (creating/managing its public share link,
            inviting by email) is an owner-only action - channel/
            share_link_*.wsl and invite_email.wsl all reject a non-owner -
            so a non-owner viewing a channel they follow/joined shouldn't
            see the entry point at all. A DM channel has exactly its two
            fixed participants and can't be shared/invited into. */}
        {owner && !c.isDirect && (
          <FeatureGate feature="channels.share_links" fallback={null}>
            <IconButton icon={Share2} onClick={() => navigate(`/channels/${c.id}/share`)} aria-label="Share channel" />
          </FeatureGate>
        )}
        {/* A public channel has a public page anyone can open. */}
        {c.isPublic && publicPagesOn && (
          <IconButton
            icon={ExternalLink}
            // A named window: the first click opens it, later clicks reuse that
            // same tab instead of piling up new ones.
            onClick={() => window.open(`/public/${encodeURIComponent(c.nickname)}`, 'zmist-public')}
            aria-label="Visit public page"
            title="Visit public page"
          />
        )}
        {/* Whoever may edit the channel gets the button right beside (i). */}
        {owner && <ChannelDataMenu channel={c} />}
        {owner && <IconButton icon={SquarePen} onClick={() => navigate(`/channels/${c.id}/edit`)} aria-label="Edit channel" title="Edit channel" />}
        <ChannelInfoButton channelId={c.id} onClick={() => navigate(`/channels/${c.id}/details`)} />
      </div>

      {/* Only needed to switch panes when they don't fit side by side -
          nothing to switch between once the xl:grid below reveals all
          three, so this stays hidden there - but only when
          threeColumnLayout is actually on; with it off the grid below
          never activates at any width, so this switcher needs to stay
          visible right along with it. */}
      <div className={threeColumnLayout ? 'xl:hidden' : ''}>
        <SegmentedControl
          options={CHANNEL_VIEW_TYPES.map((t) => ({ value: t, label: t === 'messages' ? 'Signals' : capitalize(t) }))}
          value={viewType}
          onChange={changeView}
        />
      </div>

      <div className={`flex-1 min-h-0 ${threeColumnLayout ? 'xl:grid xl:grid-cols-3 xl:gap-4' : ''}`}>
        <Pane title="Signals" active={viewType === 'messages'} threeColumnLayout={threeColumnLayout}>
          <MessagesScreen channelId={c.id} focusSignalId={focusSignalId} />
        </Pane>
        <Pane title="Charts" active={viewType === 'charts'} threeColumnLayout={threeColumnLayout}>
          <ChartScreen channelId={c.id} />
        </Pane>
        {/* No title here - ReportsScreen already renders its own (title +
            subtitle + AI-settings button), so a Pane label would just
            duplicate it. */}
        <Pane active={viewType === 'reports'} threeColumnLayout={threeColumnLayout}>
          <ReportsScreen channelId={c.id} />
        </Pane>
      </div>
    </div>
  );
}

// The channel-details "i" button, doubling as this channel's live Cue
// status light: the glyph is emerald while the signaling socket is
// connected, red when it's down, and a ring pulses around it for ~1s
// whenever a message or signal relays into this channel ("like income").
// Replaces the plain IconButton this header used to have.
function ChannelInfoButton({ channelId, onClick }: { channelId: string; onClick: () => void }) {
  const { connectionState } = useCueDiagnostics();
  const blinking = useActivityBlink(channelId);
  const connected = connectionState === 'connected';
  const tint = connected ? 'text-emerald-500' : 'text-red-500';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Channel details — Cue ${connected ? 'connected' : 'disconnected'}`}
      className={`relative inline-flex items-center justify-center rounded-full w-9 h-9 transition-colors active:scale-95 hover:bg-black/5 dark:hover:bg-white/10 ${tint}`}
    >
      {blinking && (
        <span className="absolute inset-1 rounded-full bg-emerald-500/30 motion-safe:animate-ping" aria-hidden />
      )}
      <Info size={18} strokeWidth={2.2} className="relative" />
    </button>
  );
}

// Below xl - or at any width once threeColumnLayout is off - only the
// active pane renders at all (matches the old single-tab behavior
// exactly). At xl and up, with threeColumnLayout on: xl:block overrides
// the hidden/block toggle so every pane shows regardless of `active`,
// laid out into the grid columns above; `title` (redundant with the tab
// switcher below xl, so hidden there) labels each column now that nothing
// else does - omit it for a pane whose content already provides its own
// equivalent heading.
function Pane({
  title,
  active,
  threeColumnLayout,
  children,
}: {
  title?: string;
  active: boolean;
  threeColumnLayout: boolean;
  children: ReactNode;
}) {
  const xlOverride = threeColumnLayout ? 'xl:flex' : '';
  const xlTitle = threeColumnLayout ? 'hidden xl:block' : 'hidden';
  return (
    <div className={`${active ? 'flex' : 'hidden'} ${xlOverride} flex-col gap-2 h-full min-h-0`}>
      {title && <span className={`${xlTitle} shrink-0 text-sm font-semibold text-black/50 dark:text-white/50`}>{title}</span>}
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}
